import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  IContactFormDataForAPI,
  IContactPayloadForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import {
  handleImageUpload,
  transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildContactsPayloadContactsPage } from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/helpers';
import {
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import {
  useUserManagementAPI,
  UseUserManagementAPIReturn,
} from 'src/SharedCustomHooks/UseUserManagementAPI/useUserManagementAPI';

interface UseContactFormSubmissionContactsOptions {
  onSuccess?: () => void;
  onClose?: any;
}

export interface UseContactFormSubmissionContactsReturn {
  handleSubmitContactForm: (formData: IContactFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useContactFormSubmissionContacts = ({
  onSuccess,
  onClose,
}: UseContactFormSubmissionContactsOptions = {}): UseContactFormSubmissionContactsReturn => {
  const contactAPI: UseContactAPIReturn = useContactAPI();
  const userManagementAPI: UseUserManagementAPIReturn = useUserManagementAPI();

  const adminUser: any = useSelector(selectAuthenticatedUser);

  const handleSubmitContactForm = async (
    formData: IContactFormData
  ): Promise<void> => {
    if (!formData.id && formData.id === '') {
      const errMsg = 'Cannot edit contact: Missing contact ID';
      toast.error(`🚫 ${errMsg}`, { autoClose: 8000, position: 'top-center' });
      return;
    }

    try {
      const imageUrl: string = await handleImageUpload(formData);

      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);

      const payload: IContactPayloadForAPI = buildContactsPayloadContactsPage(
        apiFormData,
        adminUser.id,
        userManagementAPI.allRoles
      );

      const { ok } = formData.id
        ? await contactAPI.updateContact(payload)
        : await contactAPI.saveContact(payload);

      if (ok) {
        onSuccess?.();
        onClose?.();
      }
    } catch (err: any) {
      console.error('Contact form submission failed:', err);
    }
  };

  return {
    handleSubmitContactForm,
    isLoading: contactAPI.isSaving,
    error: contactAPI.error,
  };
};
